import { useCallback } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { NormalizedFormValues } from '#src/app/pages/petitionDetails/shared/types';
import type { SignPetitionAsGuestResult, SignPetitionTrackingData } from '#src/app/shared/api/signature';
import { createGuestSignature } from '#src/app/shared/api/signature';
import type { SignPetitionHookInput } from '#src/app/shared/hooks/signature';
import { useTrackingSignData } from '#src/app/shared/hooks/signature';

type Options = Readonly<{
	/**
	 * the sign data `useSignData` already computed for the form
	 */
	signData: Pick<
		SignPetitionHookInput,
		| 'sourceLocation'
		| 'algorithm'
		| 'inviteRecruiterUuid'
		| 'promotionToken'
		| 'promotionPlacement'
		| 'promotionAlgorithm'
	>;
	/**
	 * extra properties for the sign event, merged into the default tracking data (same option as `useSignPetition`)
	 */
	trackingData?: Omit<SignPetitionTrackingData, 'webapp_name'>;
}>;

/**
 * Signs the petition from the sign form through `signPetitionAsGuest`. Unlike `signPetition`, traffic metadata,
 * mit/fht values and the mobile flag are derived by the middle-end, and it handles the returning-guest resubmit.
 */
export function useSignPetitionAsGuest({
	signData: { sourceLocation, algorithm, inviteRecruiterUuid, promotionToken, promotionPlacement, promotionAlgorithm },
	trackingData: extraTrackingData,
}: Options): (formData: NormalizedFormValues) => Promise<SignPetitionAsGuestResult> {
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();
	const utilityContext = useUtilityContext();
	const { trackingData: defaultTrackingData } = useTrackingSignData();

	return useCallback(
		async (formData) => {
			// the guest sign form requires an email, so this is only reachable through a bug
			if (!formData.email) throw new Error('Missing email for guest signature');

			return createGuestSignature(
				{
					signature: {
						petitionId,
						signer: {
							firstName: formData.firstName,
							lastName: formData.lastName,
							email: formData.email,
						},
						location: {
							countryCode: formData.countryCode,
							address: formData.address,
							city: formData.city,
							stateCode: formData.stateCode,
							postalCode: formData.postalCode,
						},
						preferences: {
							public: formData.public,
							shareInfoWithOrganization: formData.shareInfoWithOrganization,
							marketingCommsConsent: formData.marketingCommsConsent,
						},
						reasonForSigning: formData.reasonForSigning,
						recaptchaResponse: formData.recaptchaResponse,
						source: {
							pageContext: 'petitions_show',
							sourceLocation,
							algorithm,
							recruiterUuid: inviteRecruiterUuid,
							promotion: promotionToken
								? { token: promotionToken, placement: promotionPlacement, algorithm: promotionAlgorithm }
								: null,
						},
						trackingData: { ...defaultTrackingData, ...extraTrackingData },
					},
				},
				utilityContext,
			);
		},
		[
			petitionId,
			utilityContext,
			sourceLocation,
			algorithm,
			inviteRecruiterUuid,
			promotionToken,
			promotionPlacement,
			promotionAlgorithm,
			defaultTrackingData,
			extraTrackingData,
		],
	);
}
